/**
 * API base URL resolution and asset URL helpers (no auth / fetch).
 * Split from api.js for maintainability; re-exported from api.js for stable import paths.
 */

const trimTrailingSlash = (s) => String(s || '').replace(/\/$/, '')

const resolveApiUrl = () => {
  const raw = import.meta.env.VITE_API_URL
  if (raw != null && String(raw).trim() !== '') {
    return trimTrailingSlash(String(raw).trim())
  }
  if (import.meta.env.DEV) {
    return '/api/v1'
  }
  // Production safety: never silently default to localhost.
  // Same-origin `/api/v1` is the safest fallback if env wiring is temporarily missing.
  return '/api/v1'
}

/** Resolved API prefix including `/api/v1` (or custom VITE_API_URL). */
export const API_URL = resolveApiUrl()

const API_BASE_URL = API_URL.replace(/\/api(\/v1)?\/?$/, '')

const ABSOLUTE_URL_REGEX = /^https?:\/\//i

/** Trusted CDN/origin URLs for asset loads (full-quality originals). */
function resolveTrustedRemoteUrl(url) {
  if (!url || typeof url !== 'string') return ''
  try {
    const parsed = new URL(String(url).trim())
    const isHttps = parsed.protocol === 'https:'
    const isLocalHttp =
      parsed.protocol === 'http:' &&
      (parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1')
    if (isHttps || (import.meta.env.DEV && isLocalHttp)) return parsed.toString()
  } catch {
    // fall through
  }
  return ''
}

export function getApiConnectionErrorMessage() {
  const hostname =
    typeof globalThis !== 'undefined' && globalThis.location && typeof globalThis.location.hostname === 'string'
      ? globalThis.location.hostname
      : ''
  const onLocalHost = hostname === 'localhost' || hostname === '127.0.0.1'

  if (import.meta.env.DEV && onLocalHost) {
    return 'We couldn’t connect to the server. Start the backend from the backend folder (npm run dev), wait until it’s running, then refresh this page.'
  }
  if (onLocalHost) {
    return 'We couldn’t connect to the server. Make sure the application is running, then refresh this page.'
  }
  return 'We couldn’t connect right now. Check your internet connection and try again. If the problem continues, try again later or contact your administrator.'
}

export const getApiAssetUrl = (fileUrl) => {
  if (!fileUrl) return ''
  const raw = String(fileUrl).trim()
  if (!raw) return ''
  if (ABSOLUTE_URL_REGEX.test(raw)) return raw
  const path = raw.startsWith('/') ? raw : `/${raw}`
  return `${API_BASE_URL}${path}`
}

export const getContentFileUrl = (fileUrl) => getApiAssetUrl(fileUrl)

/**
 * Original uploaded asset URL (full-quality). Use for download and video playback source.
 * @param {object|null|undefined} content - Serialized content row
 * @returns {string}
 */
export const getContentFullAssetUrl = (content) => {
  if (!content) return ''
  const remote = resolveTrustedRemoteUrl(content.public_url)
  if (remote) return remote
  if (content.file_url) return getContentFileUrl(content.file_url)
  return ''
}

/**
 * URL for inline review (preview/thumbnail when available — lower egress than full asset).
 * Falls back to {@link getContentFullAssetUrl} when no preview exists.
 * @param {object|null|undefined} content
 * @returns {string}
 */
export const getContentDisplayUrl = (content) => {
  if (!content) return ''
  const prevRaw = content.preview_url != null ? String(content.preview_url).trim() : ''
  if (prevRaw) {
    const remote = resolveTrustedRemoteUrl(prevRaw)
    if (remote) return remote
    if (prevRaw.startsWith('/uploads/')) return getApiAssetUrl(prevRaw)
  }
  return getContentFullAssetUrl(content)
}
